import * as angular from 'angular';

import { IValueViewModel, NameBuilderConfiguration, ObjectType } from '../../models';
import { groupIdentifierSeparator } from '../../common/constants';
import { IFrequencyFormatHelperService } from '../../services/helpers';
import { ValueTypes } from '../../common/ValueTypes';
import { ISeriesConfiguration } from '../../widgets';
import { getUnitName } from '../../common';

export interface INameBuilderService {
    getName(viewModel: IValueViewModel, config: NameBuilderConfiguration, serie: ISeriesConfiguration): string;
    getValueName(value: string): string;
    getNameWithUnit(originalName: string, unitName: string, valueType: string): string;
}

export class NameBuilderService implements INameBuilderService {
    static serviceId = 'nameBuilderService';
    static $inject = [
        '$translate',
        'frequencyFormatHelperService'
    ];

    constructor(
        private _$translate: angular.translate.ITranslateService,
        private _frequencyFormatHelper: IFrequencyFormatHelperService
    ) { }

    private _conditions = [
        {
            condition: (config: NameBuilderConfiguration) => config && config.hasGroupName,
            format: (originalName: string, vm: IValueViewModel) => vm.objectType === ObjectType.Identifier
                ? originalName += vm.groupUniqueName
                : originalName
        },
        {
            condition: (c: NameBuilderConfiguration) => c && c.hasGroupName && c.hasName,
            format: (originalName: string, vm: IValueViewModel) => vm.objectType === ObjectType.Identifier
                ? `${originalName}${groupIdentifierSeparator}`
                : originalName
        },
        {
            condition: (config: NameBuilderConfiguration) => !config || config.hasName,
            format: (originalName: string, vm: IValueViewModel) => originalName
                ? `${originalName}${vm.name}`
                : vm.name
        },
        {
            condition: (config: NameBuilderConfiguration) => config && config.hasShortComment,
            format: (originalName: string, vm: IValueViewModel) => originalName
                ? `${originalName} - ${vm.shortComment}`
                : vm.shortComment
        },
        {
            condition: (config: NameBuilderConfiguration) => config && config.hasValueType,
            format: (originalName: string, vm: IValueViewModel, series: ISeriesConfiguration) => {
                let valueType = series.value;
                valueType = this._$translate.instant(this.getValueName(valueType));
                const result = originalName
                    ? `${originalName} (${valueType})`
                    : `${valueType}`;
                return result;
            }
        },
        {
            condition: (config: NameBuilderConfiguration) => config && config.hasOffset,
            format: (originalName: string, vm: IValueViewModel, serie: ISeriesConfiguration) => {
                let offset = '';
                if (serie.offset && serie.offset.interval) {
                    offset = `(${this._frequencyFormatHelper.toDuration(serie.offset).humanize(true)})`;
                }
                const newName = originalName
                    ? `${originalName} ${offset}`
                    : `${offset}`;
                return newName;
            }
        },
        {
            condition: (config: NameBuilderConfiguration) => config && config.hasUnit,
            format: (originalName: string, vm: IValueViewModel, serie: ISeriesConfiguration) => {
                return this.getNameWithUnit(originalName, vm.unitName, serie?.value);
            }
        }
    ];

    getNameWithUnit(originalName: string, unit: string, valueType: string): string {
        const unitName = getUnitName(unit, valueType);
        return originalName
            ? unitName
                ? `${originalName} [${unitName}]`
                : originalName
            : `[${unitName}]`;
    }

    getName(viewModel: IValueViewModel, config: NameBuilderConfiguration, serie: ISeriesConfiguration): string {
        let name = serie && serie.customLegend ? `${serie.customLegend} ` : '';
        if (serie) {
            this._conditions.forEach(c => {
                if (c.condition(config)) {
                    name = c.format(name, viewModel, serie);
                }
            });
            if (!name) {
                name = viewModel.name;
            }
        }
        return name;
    }

    getValueName(value: string): string {
        let valueName = value;
        if (valueName) {
            valueName = ValueTypes.getValueType(valueName);
        }
        if (!valueName) {
            valueName = 'Average';
        }
        return valueName;
    }
}